import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import {
    $getSelection,
    $isRangeSelection,
    $isElementNode,
    COMMAND_PRIORITY_EDITOR,
    ElementNode,
    $isParagraphNode,
    ParagraphNode,
    LexicalNode
} from "lexical"
import { useEffect, useRef } from "react"
import { createCommand } from "lexical"
import { $isBoxColorNode } from "../../nodes/BoxColorNode" // Import BoxColorNode check
import { $createDirectionalParagraphNode, $isDirectionalParagraphNode } from "../../nodes/DirectionalParagraphNode"
import { $isListNode, ListNode, ListItemNode } from "@lexical/list"
import {
    DirectionalListItemNode,
    $createDirectionalListItemNode,
    $isDirectionalListItemNode
} from "../../nodes/DirectionalListItemNode"
import { HeadingNode } from "@lexical/rich-text"
import { $createDirectionalHeadingNode, $isDirectionalHeadingNode } from "../../nodes/DirectionalHeadingNode"

export const CHANGE_PARAGRAPH_DIRECTION_COMMAND = createCommand<"ltr" | "rtl">("CHANGE_PARAGRAPH_DIRECTION")

// Add a key to the ElementNode to store direction attribute
declare module "lexical" {
    interface ElementNode {
        setDirection(direction: "ltr" | "rtl"): void
        getDirection(): "ltr" | "rtl" | null
    }
}

export default function DirectionPlugin(): JSX.Element | null {
    const [editor] = useLexicalComposerContext()
    const newKey = useRef({ key: null, parentKey: null })

    useEffect(() => {
        // Add direction methods to ElementNode prototype if they don't exist
        if (!ElementNode.prototype.hasOwnProperty("setDirection")) {
            ElementNode.prototype.setDirection = function (direction: "ltr" | "rtl") {
                this.getWritable().__direction = direction
                return this
            }

            ElementNode.prototype.getDirection = function () {
                return this.__direction || null
            }
        }

        // Register HTML export plugin: sync DOM direction to ParagraphNode
        const removeParagraphTransform = editor.registerNodeTransform(ParagraphNode, (node) => {
            const element = editor.getElementByKey(node.getKey())
            if (element instanceof HTMLElement) {
                const direction = element.getAttribute("dir")
                if (direction === "rtl" || direction === "ltr") {
                    ;(node as any).__direction = direction
                }
            }
        })

        // Register HTML export plugin: sync DOM direction to ListNode
        const removeListTransform = editor.registerNodeTransform(ListNode, (node) => {
            const element = editor.getElementByKey(node.getKey())
            if (element instanceof HTMLElement) {
                const direction = element.getAttribute("dir")
                if (direction === "rtl" || direction === "ltr") {
                    ;(node as any).__direction = direction
                }
            }
        })

        // Register HTML export plugin: sync DOM direction to HeadingNode
        const removeHeadingTransform = editor.registerNodeTransform(HeadingNode, (node) => {
            const element = editor.getElementByKey(node.getKey())
            if (element instanceof HTMLElement) {
                const direction = element.getAttribute("dir")
                if (direction === "rtl" || direction === "ltr") {
                    ;(node as any).__direction = direction
                }
            }
        })

        // Register the custom node
        if (!editor.hasNodes([DirectionalListItemNode])) {
            editor.registerNodeTransform(DirectionalListItemNode, (node: DirectionalListItemNode) => {
                const element = editor.getElementByKey(node.getKey())
                if (element instanceof HTMLElement) {
                    const direction = element.getAttribute("dir")
                    if (direction === "rtl" || direction === "ltr") {
                        node.setDirection(direction)
                    }
                }
            })
        }

        // Register transform for ListItemNode to inherit direction from parent list
        const removeListItemTransform = editor.registerNodeTransform(ListItemNode, (node) => {
            const parent = node.getParent()

            if ($isListNode(parent)) {
                const previous = node.getPreviousSibling()
                let parentDirection

                if (node.getDirection()) parentDirection = node.getDirection()
                else if (previous) parentDirection = (previous as any).__direction
                else if ((parent as any).getPreviousSibling() && (parent as any).getPreviousSibling().__direction)
                    parentDirection = (parent as any).getPreviousSibling().__direction
                else parentDirection = "ltr"

                if (parentDirection) {
                    if (!$isDirectionalListItemNode(node)) {
                        const directionalListItem = $createDirectionalListItemNode(parentDirection)
                        node.getChildren().forEach((child: LexicalNode) => {
                            directionalListItem.append(child)
                        })
                        node.replace(directionalListItem)
                    } else {
                        node.setDirection(parentDirection)
                    }
                } else {
                    const directionalListItem = $createDirectionalListItemNode("ltr")
                    node.getChildren().forEach((child: LexicalNode) => {
                        directionalListItem.append(child)
                    })
                    node.replace(directionalListItem)
                }
            }
        })

        return () => {
            removeParagraphTransform()
            removeListTransform()
            removeHeadingTransform()
            removeListItemTransform()
        }
    }, [editor])

    // Override exportDOM to include direction attributes for ParagraphNode and ListNode
    useEffect(() => {
        // Patch ParagraphNode exportDOM
        const baseParagraphExportDOM = ParagraphNode.prototype.exportDOM
        ParagraphNode.prototype.exportDOM = function (config) {
            const { element } = baseParagraphExportDOM.call(this, config)
            const direction = (this as any).__direction
            if (direction && element instanceof HTMLElement) {
                element.dir = direction
                element.style.direction = direction
                element.style.unicodeBidi = "embed"
            }
            return { element }
        }

        // Patch ListNode exportDOM
        const baseListExportDOM = ListNode.prototype.exportDOM
        ListNode.prototype.exportDOM = function (config) {
            const { element } = baseListExportDOM.call(this, config)
            const direction = (this as any).__direction
            if (direction && element instanceof HTMLElement) {
                element.dir = direction
                element.style.direction = direction
                element.style.unicodeBidi = "embed"
            }
            return { element }
        }

        // Patch HeadingNode exportDOM
        const baseHeadingExportDOM = HeadingNode.prototype.exportDOM
        HeadingNode.prototype.exportDOM = function (config) {
            const { element } = baseHeadingExportDOM.call(this, config)
            const direction = (this as any).__direction
            if (direction && element instanceof HTMLElement) {
                element.dir = direction
                element.style.direction = direction
                element.style.unicodeBidi = "embed"
            }
            return { element }
        }
    }, [editor])

    // Register command to handle direction changes
    useEffect(() => {
        return editor.registerCommand<"ltr" | "rtl">(
            CHANGE_PARAGRAPH_DIRECTION_COMMAND,
            (direction) => {
                editor.update(() => {
                    const selection = $getSelection()
                    if ($isRangeSelection(selection)) {
                        const nodes = selection.getNodes()

                        nodes.forEach((node) => {
                            let targetNode = node

                            // If not a paragraph, list, heading, or box color node, try parent
                            if (
                                !$isParagraphNode(targetNode) &&
                                !$isListNode(targetNode) &&
                                !$isListNode(targetNode?.getParent()?.getParent()) &&
                                !$isListNode(targetNode?.getParent()) &&
                                !$isBoxColorNode(targetNode) &&
                                !(targetNode instanceof HeadingNode)
                            ) {
                                const parent = targetNode.getParent()
                                if (parent && $isElementNode(parent)) {
                                    targetNode = parent
                                }
                            }

                            // List nodes
                            if (
                                $isListNode(targetNode?.getParent()?.getParent()) ||
                                $isListNode(targetNode?.getParent())
                            ) {
                                if ($isListNode(targetNode.getParent())) {
                                    const listNode = targetNode.getParent() as ListNode
                                    const listType = listNode.getListType()
                                    if (listType === "bullet" || listType === "number") {
                                        if (!$isDirectionalListItemNode(targetNode)) {
                                            const directionalListItem = $createDirectionalListItemNode(direction)
                                            if ($isElementNode(targetNode)) {
                                                targetNode.getChildren().forEach((child: LexicalNode) => {
                                                    directionalListItem.append(child)
                                                })

                                                targetNode.replace(directionalListItem)
                                            }
                                        } else {
                                            targetNode.setDirection(direction)
                                        }
                                    }
                                    return
                                }

                                const listNode = targetNode?.getParent()?.getParent() as ListNode
                                if (!$isListNode(listNode)) return
                                const listType = listNode.getListType()
                                if (listType === "bullet" || listType === "number") {
                                    if (!$isDirectionalListItemNode(targetNode.getParent())) {
                                        const directionalListItem = $createDirectionalListItemNode(direction)

                                        if ($isElementNode(targetNode.getParent())) {
                                            targetNode
                                                .getParent()
                                                .getChildren()
                                                .forEach((child: LexicalNode) => {
                                                    directionalListItem.append(child)
                                                })
                                            const parentNode = targetNode.getParent()

                                            parentNode.replace(directionalListItem)
                                        }
                                    } else {
                                        targetNode.getParent().setDirection(direction)
                                    }
                                }
                            }
                            // Heading nodes
                            else if (targetNode instanceof HeadingNode) {
                                if (!$isDirectionalHeadingNode(targetNode)) {
                                    const heading = targetNode as HeadingNode
                                    const tag = heading.getTag()
                                    const indent = heading.getIndent()

                                    // Get format as string
                                    let formatType = "left"
                                    const format = heading.getFormat()
                                    if (format === 1) formatType = "left"
                                    else if (format === 2) formatType = "right"
                                    else if (format === 3) formatType = "center"
                                    else if (format === 4) formatType = "justify"

                                    const directionalHeading = $createDirectionalHeadingNode(tag, direction)
                                    directionalHeading.setFormat(formatType as any)
                                    directionalHeading.setIndent(indent)
                                    directionalHeading.setDirection(direction)

                                    heading.getChildren().forEach((child) => {
                                        directionalHeading.append(child)
                                    })

                                    heading.replace(directionalHeading)
                                } else {
                                    targetNode.setDirection(direction)
                                    // Update text alignment based on format or direction
                                    const format = targetNode.getFormat()
                                    if (!(format === 1 || format === 2 || format === 3 || format === 4)) {
                                        targetNode.setFormat(direction === "rtl" ? "right" : "left")
                                    }
                                }
                            }
                            // Paragraph nodes
                            else if ($isParagraphNode(targetNode)) {
                                if (!$isDirectionalParagraphNode(targetNode)) {
                                    const paragraph = targetNode as ParagraphNode
                                    const format = paragraph.getFormat()
                                    const indent = paragraph.getIndent()

                                    const directionalParagraph = $createDirectionalParagraphNode(direction)

                                    if (format === 1) directionalParagraph.setFormat("left")
                                    else if (format === 2) directionalParagraph.setFormat("right")

                                    directionalParagraph.setIndent(indent)
                                    directionalParagraph.setDirection(direction)

                                    paragraph.getChildren().forEach((child) => {
                                        directionalParagraph.append(child)
                                    })

                                    paragraph.replace(directionalParagraph)
                                } else {
                                    targetNode.setDirection(direction)
                                    targetNode.setFormat(direction === "rtl" ? "right" : "left")
                                }
                            }
                            // BoxColorNode nodes
                            else if ($isBoxColorNode(targetNode)) {
                                targetNode.setDirection(direction)
                                targetNode.setFormat(direction === "rtl" ? "right" : "left")

                                const element = editor.getElementByKey(targetNode.getKey())
                                if (element instanceof HTMLElement) {
                                    element.dir = direction
                                    element.style.direction = direction
                                    element.style.unicodeBidi = "embed"
                                    element.style.textAlign = direction === "rtl" ? "right" : "left"
                                }
                            }
                        })
                    }
                    return true
                })
                return true
            },
            COMMAND_PRIORITY_EDITOR
        )
    }, [editor])

    // Ensure root element has proper text direction styling
    useEffect(() => {
        const rootElement = editor.getRootElement()
        if (rootElement) {
            rootElement.style.direction = "inherit"
            rootElement.style.unicodeBidi = "embed"
        }

        return () => {
            const rootElement = editor.getRootElement()
            if (rootElement) {
                rootElement.style.direction = ""
                rootElement.style.unicodeBidi = ""
            }
        }
    }, [editor])

    return null
}
